import { useCallback } from 'react';
import * as Cesium from 'cesium';

type Vec3 = [number, number, number];
const vecMag = (v: Vec3): number => Math.sqrt(v[0]*v[0] + v[1]*v[1] + v[2]*v[2]);
const vecCross = (a: Vec3, b: Vec3): Vec3 => [
  a[1]*b[2] - a[2]*b[1],
  a[2]*b[0] - a[0]*b[2],
  a[0]*b[1] - a[1]*b[0]
];
const vecDot = (a: Vec3, b: Vec3): number => a[0]*b[0] + a[1]*b[1] + a[2]*b[2];
const vecSub = (a: Vec3, b: Vec3): Vec3 => [a[0]-b[0], a[1]-b[1], a[2]-b[2]];
const vecScale = (v: Vec3, s: number): Vec3 => [v[0]*s, v[1]*s, v[2]*s];

const EARTH_MU_KM3_S2 = 398600.4418;
const EPS = 1e-9;

function safeAcos(v: number): number {
  return Math.acos(Math.min(1, Math.max(-1, v)));
}

function normalizeDegrees(deg: number): number {
  let d = ((deg % 360) + 360) % 360;
  if (d >= 359.995) d = 0;
  return d;
}

interface KeplerianOutput {
  semiMajorAxis: number;
  eccentricity: number;
  inclination: number;
  RAAN: number;
  AOP: number;
  TA: number;
  meanAnomaly: number;
}

function cartesianToKeplerian(r: Vec3, v: Vec3): KeplerianOutput | null {
  const rMag = vecMag(r);
  const vMag = vecMag(v);
  if (rMag <= EPS || vMag <= EPS) return null;

  const hVec = vecCross(r, v);
  const hMag = vecMag(hVec);
  if (hMag <= EPS) return null;

  const nVec = vecCross([0, 0, 1], hVec);
  const nMag = vecMag(nVec);
  const eVec = vecSub(vecScale(vecCross(v, hVec), 1 / EARTH_MU_KM3_S2), vecScale(r, 1 / rMag));
  let eMag = vecMag(eVec);
  const energy = (vMag * vMag) / 2 - EARTH_MU_KM3_S2 / rMag;
  if (Math.abs(energy) <= EPS) return null;

  const semiMajorAxis = -EARTH_MU_KM3_S2 / (2 * energy);
  if (!Number.isFinite(semiMajorAxis) || semiMajorAxis <= 0) return null;

  let inclination = (safeAcos(hVec[2] / hMag) * 180) / Math.PI;

  const TOL = 1e-5;
  const isCircular = eMag < TOL;
  const isEquatorial = inclination < TOL || Math.abs(inclination - 180) < TOL;

  if (isCircular) {
    eMag = 0;
  }

  let RAAN = 0;
  let AOP = 0;
  let TA = 0;

  if (isEquatorial && isCircular) {
    RAAN = 0;
    AOP = 0;
    TA = normalizeDegrees((Math.atan2(r[1], r[0]) * 180) / Math.PI);
  } else if (isEquatorial) {
    RAAN = 0;
    AOP = normalizeDegrees((Math.atan2(eVec[1], eVec[0]) * 180) / Math.PI);
    const y = vecDot(vecCross(eVec, r), hVec) / (eMag * rMag * hMag);
    const x = vecDot(eVec, r) / (eMag * rMag);
    TA = normalizeDegrees((Math.atan2(y, x) * 180) / Math.PI);
  } else if (isCircular) {
    RAAN = nMag > EPS ? normalizeDegrees((Math.atan2(nVec[1], nVec[0]) * 180) / Math.PI) : 0;
    AOP = 0;
    const y = vecDot(vecCross(nVec, r), hVec) / (nMag * rMag * hMag);
    const x = vecDot(nVec, r) / (nMag * rMag);
    TA = normalizeDegrees((Math.atan2(y, x) * 180) / Math.PI);
  } else {
    RAAN = nMag > EPS ? normalizeDegrees((Math.atan2(nVec[1], nVec[0]) * 180) / Math.PI) : 0;
    const yAop = vecDot(vecCross(nVec, eVec), hVec) / (nMag * eMag * hMag);
    const xAop = vecDot(nVec, eVec) / (nMag * eMag);
    AOP = normalizeDegrees((Math.atan2(yAop, xAop) * 180) / Math.PI);
    const yTa = vecDot(vecCross(eVec, r), hVec) / (eMag * rMag * hMag);
    const xTa = vecDot(eVec, r) / (eMag * rMag);
    TA = normalizeDegrees((Math.atan2(yTa, xTa) * 180) / Math.PI);
  }

  // Calculate Mean Anomaly
  const taRad = (TA * Math.PI) / 180;
  const sqrtTerm = Math.sqrt((1 - eMag) / (1 + eMag));
  const E = 2 * Math.atan2(sqrtTerm * Math.sin(taRad / 2), Math.cos(taRad / 2));
  const M = E - eMag * Math.sin(E);
  const meanAnomaly = normalizeDegrees((M * 180) / Math.PI);

  return {
    semiMajorAxis,
    eccentricity: eMag,
    inclination,
    RAAN,
    AOP,
    TA,
    meanAnomaly,
  };
}

function satFixedPos(satEntity: Cesium.Entity, time: Cesium.JulianDate): Cesium.Cartesian3 | null {
  if (!satEntity.position) return null;
  return satEntity.position.getValueInReferenceFrame(time, Cesium.ReferenceFrame.FIXED, new Cesium.Cartesian3()) ?? null;
}

function satInertialPos(satEntity: Cesium.Entity, time: Cesium.JulianDate): Cesium.Cartesian3 | null {
  if (!satEntity.position) return null;
  return satEntity.position.getValueInReferenceFrame(time, Cesium.ReferenceFrame.INERTIAL, new Cesium.Cartesian3()) ?? null;
}

export function useClockManager(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  modelClockWindowRef: React.MutableRefObject<any>,
  getDefaultModelEpoch: () => number,
) {
  const setClockPlaying = useCallback((playing: boolean) => {
    const v = viewerRef.current;
    if (!v) return;
    if (playing) {
      const currentToStop = Cesium.JulianDate.secondsDifference(v.clock.stopTime, v.clock.currentTime);
      if (currentToStop <= 0.25) {
        v.clock.currentTime = v.clock.startTime.clone();
      }
      if (!Number.isFinite(v.clock.multiplier) || v.clock.multiplier === 0) v.clock.multiplier = 1;
    }
    v.clock.shouldAnimate = playing;
  }, []);

  const setClockMultiplier = useCallback((multiplier: number) => {
    const v = viewerRef.current;
    if (v) v.clock.multiplier = multiplier;
  }, []);

  const resetClockToNow = useCallback(() => {
    const v = viewerRef.current;
    if (v) {
      v.clock.currentTime = Cesium.JulianDate.fromDate(new Date(getDefaultModelEpoch() * 1000));
      v.clock.shouldAnimate = false;
    }
  }, [getDefaultModelEpoch]);

  const resetClockToModelStart = useCallback(() => {
    const v = viewerRef.current;
    if (!v) return;
    const startTime = modelClockWindowRef.current
      ? Cesium.JulianDate.fromDate(new Date(modelClockWindowRef.current.startEpoch * 1000))
      : v.clock.startTime.clone();
    v.clock.currentTime = startTime;
    v.clock.shouldAnimate = false;
    if (secondaryViewerRef.current && !secondaryViewerRef.current.isDestroyed()) {
      secondaryViewerRef.current.clock.currentTime = startTime.clone();
      secondaryViewerRef.current.clock.shouldAnimate = false;
    }
  }, []);

  const getLiveSatelliteState = useCallback((satId: string) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return null;

    const satEntity = ds.entities.getById(satId);
    if (!satEntity) return null;

    const currentTime = v.clock.currentTime;
    const posFixed = satFixedPos(satEntity, currentTime);
    if (!posFixed) return null;

    const carto = Cesium.Cartographic.fromCartesian(posFixed);
    if (!carto || !isFinite(carto.latitude) || !isFinite(carto.longitude) || !isFinite(carto.height)) return null;

    const posInertial = satInertialPos(satEntity, currentTime);

    // Determine safe times t0 and t1 within clock boundaries to prevent extrapolation holding
    let t0 = Cesium.JulianDate.addSeconds(currentTime, -0.5, new Cesium.JulianDate());
    let t1 = Cesium.JulianDate.addSeconds(currentTime, 0.5, new Cesium.JulianDate());

    const startJD = v.clock.startTime;
    const stopJD = v.clock.stopTime;

    if (startJD && Cesium.JulianDate.compare(t0, startJD) < 0) {
      t0 = startJD.clone();
      t1 = Cesium.JulianDate.addSeconds(startJD, 1.0, new Cesium.JulianDate());
    } else if (stopJD && Cesium.JulianDate.compare(t1, stopJD) > 0) {
      t1 = stopJD.clone();
      t0 = Cesium.JulianDate.addSeconds(stopJD, -1.0, new Cesium.JulianDate());
    }

    const dt = Cesium.JulianDate.secondsDifference(t1, t0);
    const p0 = satInertialPos(satEntity, t0);
    const p1 = satInertialPos(satEntity, t1);

    let velocityKmS: number | null = null;
    let kep: KeplerianOutput | null = null;

    if (posInertial && p0 && p1 && dt > 0) {
      // Difference velocity vector in m/s
      const vX = (p1.x - p0.x) / dt;
      const vY = (p1.y - p0.y) / dt;
      const vZ = (p1.z - p0.z) / dt;

      const vVec: Vec3 = [vX / 1000, vY / 1000, vZ / 1000]; // in km/s
      const rVec: Vec3 = [posInertial.x / 1000, posInertial.y / 1000, posInertial.z / 1000]; // in km

      velocityKmS = vecMag(vVec);
      kep = cartesianToKeplerian(rVec, vVec);
    }

    return {
      latitudeDeg: Cesium.Math.toDegrees(carto.latitude),
      longitudeDeg: Cesium.Math.toDegrees(carto.longitude),
      altitudeKm: carto.height / 1000,
      velocityKmS,
      epochSec: Cesium.JulianDate.toDate(currentTime).getTime() / 1000,
      semiMajorAxis: kep?.semiMajorAxis ?? null,
      eccentricity: kep?.eccentricity ?? null,
      inclination: kep?.inclination ?? null,
      raan: kep?.RAAN ?? null,
      aop: kep?.AOP ?? null,
      ta: kep?.TA ?? null,
      meanAnomaly: kep?.meanAnomaly ?? null,
    };
  }, []);

  const initClockFromModel = useCallback((startEpoch: number, endEpoch: number) => {
    const v = viewerRef.current;
    if (!v) return;
    modelClockWindowRef.current = { startEpoch, endEpoch };
    const startTime = Cesium.JulianDate.fromDate(new Date(startEpoch * 1000));
    const stopTime = Cesium.JulianDate.fromDate(new Date(endEpoch * 1000));
    v.clock.startTime = startTime;
    v.clock.stopTime = stopTime;
    v.clock.clockRange = Cesium.ClockRange.CLAMPED;

    let shouldSetToStart = true;
    if (v.clock.currentTime) {
      try {
        const curDate = Cesium.JulianDate.toDate(v.clock.currentTime);
        const curSec = curDate.getTime() / 1000;
        if (Number.isFinite(curSec) && curSec >= startEpoch && curSec <= endEpoch) {
          shouldSetToStart = false;
        }
      } catch {
        shouldSetToStart = true;
      }
    }

    if (shouldSetToStart) {
      v.clock.currentTime = startTime.clone();
    }

    if (secondaryViewerRef.current && !secondaryViewerRef.current.isDestroyed()) {
      secondaryViewerRef.current.clock.startTime = startTime.clone();
      secondaryViewerRef.current.clock.stopTime = stopTime.clone();
      secondaryViewerRef.current.clock.currentTime = v.clock.currentTime.clone();
      secondaryViewerRef.current.clock.clockRange = Cesium.ClockRange.CLAMPED;
    }
  }, []);

  const setClockTime = useCallback((date: Date) => {
    const v = viewerRef.current;
    if (!v) return;
    v.clock.currentTime = Cesium.JulianDate.fromDate(date);
  }, []);

  return {
    setClockPlaying,
    setClockMultiplier,
    resetClockToNow,
    resetClockToModelStart,
    getLiveSatelliteState,
    initClockFromModel,
    setClockTime,
  };
}
